import { faClose } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MDXEditor, type MDXEditorMethods } from '@mdxeditor/editor';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Stack,
  Switch
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form';

import LoadingIndicator from '@/components/LoadingIndicator';
import {
  FULL_MONTH_YEAR_FORMAT,
  YEAR_MONTH_FORMAT
} from '@/constants/date.constants';
import { MDX_EDITOR_CONFIG } from '@/pages/Ticket/TicketCreate/TicketCreate.constants';

import { BLANK_REPORT_INSIGHTS } from './SocMonthlySummaryDialog.constants';
import {
  useFetchReportInsights,
  useSaveReportInsights
} from './SocMonthlySummaryDialog.hooks';
import useStyles from './SocMonthlySummaryDialog.styles';
import type {
  SocMonthlySummaryDialogFormValues,
  SocMonthlySummaryDialogProps
} from './SocMonthlySummaryDialog.types';

let prevSelectedMonth = '';

const SocMonthlySummaryDialog = (props: SocMonthlySummaryDialogProps) => {
  const { open, setOpen } = props;
  const { classes } = useStyles();
  const { mode: themeMode } = useColorScheme();

  const { data, isFetching } = useFetchReportInsights();
  const {
    mutate: saveReportInsights,
    isPending,
    isSuccess
  } = useSaveReportInsights();
  const body_1_ref = useRef<MDXEditorMethods>(null);
  const body_2_ref = useRef<MDXEditorMethods>(null);

  const form = useForm({
    defaultValues: BLANK_REPORT_INSIGHTS
  });

  const { handleSubmit, reset, control } = form;
  const reportInsightsMonth = useWatch({
    control,
    name: 'reportInsightsMonth'
  });
  const isPrevMonth = !!reportInsightsMonth?.isBefore(dayjs().startOf('month'));

  // Only close the dialog if the save operation was successful
  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      prevSelectedMonth = '';
    }
  }, [isSuccess]);

  // check if there is already a report insights for the selected month and set the form values accordingly
  useEffect(() => {
    const selectedMonth = reportInsightsMonth?.format?.(YEAR_MONTH_FORMAT);

    if (selectedMonth === prevSelectedMonth || !data || !body_1_ref?.current) {
      return;
    }

    const reportInsights = data?.find(
      (item) => item.valid_for_month === selectedMonth
    );

    if (reportInsights) {
      reset({
        ...reportInsights,
        body_2: reportInsights.body_2 || '',
        reportInsightsMonth: dayjs(reportInsights.valid_for_month)
      });

      body_1_ref?.current?.setMarkdown(reportInsights.body_1 || '');
      body_2_ref?.current?.setMarkdown(reportInsights.body_2 || '');
    } else {
      reset({
        ...BLANK_REPORT_INSIGHTS,
        reportInsightsMonth: dayjs(reportInsightsMonth || new Date())
      });

      body_1_ref?.current?.setMarkdown('');
      body_2_ref?.current?.setMarkdown('');
    }

    prevSelectedMonth = selectedMonth; // Update the previous selected month
  }, [reportInsightsMonth, data, body_1_ref?.current]);

  const onSubmit: SubmitHandler<SocMonthlySummaryDialogFormValues> = async (
    formData: SocMonthlySummaryDialogFormValues
  ) => {
    saveReportInsights({
      reportInsights: {
        ...formData,
        valid_for_month: dayjs(formData.reportInsightsMonth).format(
          YEAR_MONTH_FORMAT
        )
      },
      id: formData.id || null
    });
  };

  const handleClose = () => {
    setOpen(false);
    prevSelectedMonth = '';
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        SOC Monthly Report Summary
        <Button color="secondary" variant="text" onClick={handleClose}>
          <FontAwesomeIcon icon={faClose} size="xl" />
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        <LoadingIndicator isLoading={isPending || isFetching} />
        {isPrevMonth && (
          <Alert severity="warning" sx={{ marginBottom: '16px' }}>
            You can only view but not edit report insights for previous months.
          </Alert>
        )}
        <FormProvider {...form}>
          <form noValidate>
            <Stack direction="column" spacing={3}>
              <div>
                <InputLabel
                  sx={{ paddingBottom: '8px' }}
                  htmlFor="month-select-input-label"
                >
                  Month
                </InputLabel>
                <FormControl sx={{ width: '200px' }}>
                  <Controller
                    control={control}
                    name="reportInsightsMonth"
                    render={({ field, fieldState }) => (
                      <DatePicker
                        views={['month', 'year']}
                        minDate={dayjs().subtract(12, 'month')}
                        maxDate={dayjs().add(10, 'year')}
                        value={field.value || null}
                        onChange={(newValue) => {
                          field.onChange(newValue ? dayjs(newValue) : null);
                        }}
                        slotProps={{
                          textField: {
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message || null
                          }
                        }}
                      />
                    )}
                  />
                </FormControl>
              </div>
              <div>
                <InputLabel
                  sx={{ paddingBottom: '8px' }}
                  htmlFor="soc-insights-editor"
                >
                  SOC Operational Insights
                </InputLabel>
                <FormControl id="soc-insights-editor" fullWidth>
                  <Controller
                    control={control}
                    name="body_1"
                    rules={{
                      required: true,
                      validate: (value) => value?.trim() !== ''
                    }}
                    render={({ field, fieldState }) => (
                      <MDXEditor
                        className={`${themeMode}-theme dark-editor ${classes.mdxEditor} ${
                          fieldState.error ? `${classes.error}` : ''
                        }`}
                        contentEditableClassName="cm-richView"
                        markdown=""
                        plugins={MDX_EDITOR_CONFIG}
                        ref={body_1_ref}
                        suppressHtmlProcessing
                        onChange={(value: string) => field.onChange(value)}
                      />
                    )}
                  />
                </FormControl>
              </div>
              <div>
                <InputLabel
                  sx={{ paddingBottom: '8px' }}
                  htmlFor="soc-perspective-editor"
                >
                  SOC Perspective - Trends & Observations
                </InputLabel>
                <FormControl id="soc-perspective-editor" fullWidth>
                  <Controller
                    control={control}
                    name="body_2"
                    rules={{
                      required: true,
                      validate: (value) => value?.trim() !== ''
                    }}
                    render={({ field, fieldState }) => (
                      <MDXEditor
                        className={`${themeMode}-theme dark-editor ${classes.mdxEditor} ${
                          fieldState.error ? `${classes.error}` : ''
                        }`}
                        contentEditableClassName="cm-richView"
                        markdown=""
                        plugins={MDX_EDITOR_CONFIG}
                        ref={body_2_ref}
                        suppressHtmlProcessing
                        onChange={(value: string) => field.onChange(value)}
                      />
                    )}
                  />
                </FormControl>
              </div>
              <div>
                <FormControl id="publish-summary" fullWidth>
                  <Controller
                    control={control}
                    name="published"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={isPrevMonth}
                            checked={field.value || false}
                            onChange={(_event, checked) => {
                              field.onChange(checked);
                            }}
                          />
                        }
                        label={`Publish in ${dayjs(reportInsightsMonth).format(FULL_MONTH_YEAR_FORMAT)} report`}
                      />
                    )}
                  />
                </FormControl>
              </div>
            </Stack>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isPrevMonth}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SocMonthlySummaryDialog;
